import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './styled-select.module.scss';
import { ArrowUpIcon } from '../icons/arrow-up-icon';
import { ArrowDownIcon } from '../icons/arrow-down-icon';
import { VerticalOption } from '../types';

interface StyledSelectProps {
  selectOptions: VerticalOption[];
  selectedValue: VerticalOption;
  setSelectedValue: (value: VerticalOption) => void;
}

export const StyledSelect = ({
  selectOptions,
  selectedValue,
  setSelectedValue,
}: StyledSelectProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState<number>(-1);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionsRef = useRef<Array<HTMLLIElement | null>>([]);

  /* ---------- close if you click outside ---------- */
  useEffect(() => {
    if (!isOpen) return; // run only while menu is open

    const handleOutside = (e: MouseEvent | TouchEvent) => {
      // if the click target is not inside the component → close
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    // use both mousedown & touchstart so it also works on mobile browsers
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    // cleanup
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [isOpen]);

  /* ---------- open & give focus to the selected/first option ---------- */
  const openAndFocus = useCallback(() => {
    setIsOpen(true);
    // wait next paint → focus
    requestAnimationFrame(() => {
      const selIdx =
        selectOptions.findIndex((o) => o.label === selectedValue.label) ?? 0;
      setHighlightedIdx(selIdx);
      optionsRef.current[selIdx]?.focus();
    });
  }, [selectOptions, selectedValue]);

  /* ---------- keyboard on the trigger ---------- */
  const handleTriggerKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        e.preventDefault();
        openAndFocus();
        break;
      default:
        break;
    }
  };

  /* ---------- keyboard in the listbox ---------- */
  const handleListKey = (e: React.KeyboardEvent<HTMLUListElement>) => {
    if (!isOpen) return;

    const lastIdx = selectOptions.length - 1;

    const move = (next: number) => {
      setHighlightedIdx(next);
      optionsRef.current[next]?.focus();
    };

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        move(highlightedIdx === lastIdx ? 0 : highlightedIdx + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        move(highlightedIdx <= 0 ? lastIdx : highlightedIdx - 1);
        break;
      case 'Home':
        e.preventDefault();
        move(0);
        break;
      case 'End':
        e.preventDefault();
        move(lastIdx);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleSelect(selectOptions[highlightedIdx]);
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
        break;
      default:
        break;
    }
  };

  /* ---------- select helper ---------- */
  const handleSelect = (option: VerticalOption) => {
    setSelectedValue(option);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div ref={wrapperRef} className={styles.styledSelect}>
      <button
        type="button"
        ref={triggerRef}
        className={styles.selectTrigger}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onKeyDown={handleTriggerKey}
        onClick={() => (isOpen ? setIsOpen(false) : openAndFocus())}
      >
        <span className={styles.iconAndLabel}>
          {selectedValue.icon && (
            <span className={styles.selectedIcon}>{selectedValue.icon}</span>
          )}
          <span className={styles.selectedLabel}>{selectedValue.label}</span>
        </span>
        <span>{isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}</span>
      </button>

      {isOpen && (
        <ul
          role="listbox"
          aria-activedescendant={
            highlightedIdx >= 0 ? `option-${highlightedIdx}` : undefined
          }
          className={styles.selectOptions}
          tabIndex={-1}
          onKeyDown={handleListKey}
        >
          {selectOptions.map((option, idx) => (
            <li
              id={`option-${idx}`}
              key={idx}
              role="option"
              aria-selected={selectedValue.label === option.label}
              tabIndex={highlightedIdx === idx ? 0 : -1}
              ref={(el) => (optionsRef.current[idx] = el)}
              className={
                selectedValue.label === option.label
                  ? styles.selectedOption
                  : styles.selectOption
              }
              onClick={() => handleSelect(option)}
              onMouseEnter={() => setHighlightedIdx(idx)}
            >
              {option.icon && (
                <span
                  className={
                    selectedValue.label === option.label
                      ? styles.selectedIcon
                      : styles.icon
                  }
                >
                  {option.icon}
                </span>
              )}
              <span
                className={
                  selectedValue.label === option.label
                    ? styles.selectedLabel
                    : styles.selectLabel
                }
              >
                {option.label}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
